<template>
  <div class="space-y-6">
    <div class="flex justify-between items-center">
      <div>
        <h1 class="text-2xl font-bold text-gray-900">GitHub Pull Requests</h1>
        <p class="mt-1 text-sm text-gray-600">
          Apache Kafka PMC 成员的开放 Pull Requests
        </p>
      </div>
      <div class="flex items-center space-x-4">
        <div class="flex items-center space-x-2">
          <label class="text-sm font-medium text-gray-700">排序方式:</label>
          <select
            v-model="sortBy"
            @change="handleSortChange"
            class="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="updated">更新时间</option>
            <option value="created">创建时间</option>
          </select>
        </div>
        <div class="flex items-center space-x-2">
          <label class="text-sm font-medium text-gray-700">成员过滤:</label>
          <select
            v-model="memberFilter"
            class="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">所有成员</option>
            <option value="active">仅活跃成员</option>
            <option value="inactive">仅不活跃成员</option>
          </select>
        </div>
        <button
          @click="refreshData"
          :disabled="loading"
          class="btn btn-primary flex items-center space-x-2"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
          </svg>
          <span>刷新</span>
        </button>
      </div>
    </div>

    <!-- 统计信息 -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
      <div class="card p-4">
        <div class="text-2xl font-bold text-primary-600">{{ totalPRs }}</div>
        <div class="text-sm text-gray-600">总PR数</div>
      </div>
      <div class="card p-4">
        <div class="text-2xl font-bold text-blue-600">{{ openPRs }}</div>
        <div class="text-sm text-gray-600">开放PR</div>
      </div>
      <div class="card p-4">
        <div class="text-2xl font-bold text-green-600">{{ reviewablePRs }}</div>
        <div class="text-sm text-gray-600">待审核</div>
      </div>
      <div class="card p-4">
        <div class="text-2xl font-bold text-orange-600">{{ draftPRs }}</div>
        <div class="text-sm text-gray-600">草稿</div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="text-center py-8">
      <div class="inline-flex items-center space-x-2">
        <svg class="animate-spin h-5 w-5 text-primary-600" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span class="text-gray-600">加载中...</span>
      </div>
    </div>

    <!-- PRs列表 -->
    <div v-else-if="sortedPRs.length > 0" class="space-y-4">
      <div
        v-for="pr in sortedPRs"
        :key="pr.id"
        class="card p-6 hover:shadow-md transition-shadow"
      >
        <div class="flex items-start justify-between">
          <div class="flex-1 min-w-0">
            <div class="flex items-center space-x-3 mb-2">
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                    :class="getStateColor(pr.state)">
                {{ pr.state }}
              </span>
              <span v-if="pr.labels && pr.labels.length > 0" class="flex space-x-1">
                <span
                  v-for="label in pr.labels.slice(0, 3)"
                  :key="label.name"
                  class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                  :style="{ backgroundColor: `#${label.color}`, color: getContrastColor(label.color) }"
                >
                  {{ label.name }}
                </span>
              </span>
            </div>
            
            <h3 class="text-lg font-medium text-gray-900 mb-2">
              <a :href="pr.url" target="_blank" class="hover:text-primary-600">
                #{{ pr.number }}: {{ pr.title }}
              </a>
            </h3>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 mb-3">
              <div>
                <span class="font-medium">作者:</span>
                <a :href="pr.authorUrl" target="_blank" class="text-primary-600 hover:text-primary-700">
                  {{ pr.author }}
                </a>
                <span v-if="pr.isAuthorPMC" class="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                  PMC
                </span>
              </div>
              <div v-if="pr.assignees && pr.assignees.length > 0">
                <span class="font-medium">负责人:</span>
                <span v-for="(assignee, index) in pr.assignees" :key="assignee.login">
                  <a :href="assignee.url" target="_blank" class="text-primary-600 hover:text-primary-700">
                    {{ assignee.login }}
                  </a>
                  <span v-if="index < pr.assignees.length - 1">, </span>
                </span>
              </div>
              <div>
                <span class="font-medium">创建时间:</span>
                {{ formatDate(pr.createdAt) }}
              </div>
              <div>
                <span class="font-medium">更新时间:</span>
                {{ formatDate(pr.updatedAt) }}
              </div>
              <!-- PMC参与信息 -->
              <div v-if="pr.pmcMembers && pr.pmcMembers.length > 0" class="md:col-span-2">
                <span class="font-medium">PMC成员参与:</span>
                <div class="mt-1 flex flex-wrap gap-2">
                  <span v-for="member in pr.pmcMembers" :key="member" class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                    {{ member }}
                  </span>
                </div>
                <div v-if="pr.pmcInvolvement && pr.pmcInvolvement.length > 0" class="mt-1 text-xs text-gray-500">
                  参与方式: {{ pr.pmcInvolvement.join(', ') }}
                </div>
              </div>
            </div>

            <!-- 代码变更统计 -->
            <div v-if="pr.additions !== undefined || pr.deletions !== undefined" class="flex items-center space-x-4 text-xs text-gray-600">
              <span v-if="pr.additions !== undefined" class="text-green-600">
                +{{ pr.additions }} 增加
              </span>
              <span v-if="pr.deletions !== undefined" class="text-red-600">
                -{{ pr.deletions }} 删除
              </span>
              <span v-if="pr.changedFiles !== undefined">
                {{ pr.changedFiles }} 文件变更
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-else class="text-center py-12">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"></path>
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900">暂无开放的PR</h3>
      <p class="mt-1 text-sm text-gray-500">
        {{ loading ? '正在加载...' : '点击刷新按钮获取最新数据' }}
      </p>
      <div v-if="!loading && githubPRs.length === 0" class="mt-4">
        <p class="text-xs text-gray-400">
          提示：GitHub API 可能需要认证令牌才能获取数据
        </p>
      </div>
    </div>

    <!-- 错误状态 -->
    <div v-if="error" class="bg-red-50 border border-red-200 rounded-md p-4">
      <div class="flex">
        <svg class="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
        </svg>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">加载失败</h3>
          <p class="mt-1 text-sm text-red-700">{{ error }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useAppStore } from '@/stores'

const store = useAppStore()
const sortBy = ref('updated')
const memberFilter = ref('all')

// 使用 storeToRefs 保持响应式
const { loading, error, githubPRs, totalPRs, defaultPMCMembers } = storeToRefs(store)

// 计算属性
const sortedPRs = computed(() => {
  if (!githubPRs.value) return []

  let prs = [...githubPRs.value]

  // 根据成员活跃度过滤
  if (memberFilter.value !== 'all') {
    const activeMemberIds = defaultPMCMembers.value
      .filter(member => member.activityStatus === 'active')
      .map(member => member.githubId)
      .filter(id => id)

    const inactiveMemberIds = defaultPMCMembers.value
      .filter(member => member.activityStatus === 'inactive')
      .map(member => member.githubId)
      .filter(id => id)

    if (memberFilter.value === 'active') {
      prs = prs.filter(pr => activeMemberIds.includes(pr.author))
    } else if (memberFilter.value === 'inactive') {
      prs = prs.filter(pr => inactiveMemberIds.includes(pr.author))
    }
  }

  // 排序
  return prs.sort((a, b) => {
    const dateA = new Date(sortBy.value === 'updated' ? a.updatedAt : a.createdAt)
    const dateB = new Date(sortBy.value === 'updated' ? b.updatedAt : b.createdAt)
    return dateB - dateA
  })
})

const openPRs = computed(() => {
  if (!githubPRs.value || !Array.isArray(githubPRs.value)) return 0
  return githubPRs.value.filter(pr => pr.state === 'open').length
})

const reviewablePRs = computed(() => {
  if (!githubPRs.value || !Array.isArray(githubPRs.value)) return 0
  return githubPRs.value.filter(pr =>
    pr.state === 'open' && pr.reviewers && pr.reviewers.length > 0
  ).length
})

const draftPRs = computed(() => {
  if (!githubPRs.value || !Array.isArray(githubPRs.value)) return 0
  return githubPRs.value.filter(pr => pr.draft === true).length
})

// 方法
const formatDate = (dateString) => {
  if (!dateString) return '未知'
  
  const date = new Date(dateString)
  const now = new Date()
  const diffInHours = (now - date) / (1000 * 60 * 60)
  
  if (diffInHours < 24) {
    return `${Math.floor(diffInHours)}小时前`
  } else if (diffInHours < 24 * 7) {
    const diffInDays = Math.floor(diffInHours / 24)
    return `${diffInDays}天前`
  } else {
    return date.toLocaleDateString('zh-CN')
  }
}

const getStateColor = (state) => {
  const colors = {
    'open': 'bg-green-100 text-green-800',
    'closed': 'bg-red-100 text-red-800',
    'merged': 'bg-purple-100 text-purple-800'
  }
  return colors[state] || 'bg-gray-100 text-gray-800'
}

const getContrastColor = (hexColor) => {
  // 计算对比色以确保标签文字可读
  if (!hexColor) return '#000000'
  const cleanHex = hexColor.replace('#', '')
  const r = parseInt(cleanHex.substring(0, 2), 16)
  const g = parseInt(cleanHex.substring(2, 4), 16)
  const b = parseInt(cleanHex.substring(4, 6), 16)
  const brightness = (r * 299 + g * 587 + b * 114) / 1000
  return brightness > 128 ? '#000000' : '#ffffff'
}

const handleSortChange = () => {
  // 排序变化时的处理逻辑
}

const refreshData = async () => {
  try {
    await store.fetchGitHubPRs(true)
  } catch (error) {
    console.error('Failed to refresh GitHub PRs data:', error)
  }
}

// 生命周期
onMounted(() => {
  if (githubPRs.value.length === 0) {
    refreshData()
  }
})
</script> 